<template>
  <div class="rating-sheet-wrapper">
    <div class="rating-sheet-backdrop"></div>

    <section
      class="rating-sheet"
      role="dialog"
      aria-modal="true"
    >
      <ModalCloseButton
        class="dialog-minimize-btn"
        @close="$emit('confirmAction')"
      />

      <div class="sheet-content">
        <div class="sheet-header">
          <h3 class="sheet-title">
            {{ $t('rating_transaction.title') }}
          </h3>
          <p class="sheet-subtitle">
            {{ pointName }}
            <span v-if="token"> • {{ token }}</span>
          </p>
        </div>

        <div
          v-if="!showThanksMessage"
          class="rating-form"
        >
          <div class="stars-row">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              class="star-btn"
              :aria-label="`Avaliar com ${star} estrelas`"
              @click="rating = star"
            >
              <font-awesome-icon
                icon="fa-solid fa-star"
                :class="star <= rating ? 'star-on' : 'star-off'"
                class="star-icon"
              />
            </button>
          </div>

          <textarea
            v-model="feedback"
            class="feedback-input"
            :placeholder="$t('rating_transaction.feedback')"
          ></textarea>

          <div class="actions-row">
            <BButton
              class="send-btn"
              variant="primary"
              :disabled="feedback.length === 0 && rating === null"
              @click="confirmAction"
            >
              {{ $t('rating_transaction.send') }}
            </BButton>
          </div>
        </div>

        <div
          v-else
          class="thanks-state"
        >
          <font-awesome-icon
            icon="fa-solid fa-circle-check"
            class="thanks-icon"
          />
          <p class="thanks-text">
            {{ $t('evaluation_box.text_2') }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import API from '@/services/api'
  import handleError from '@/services/handleError'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import { UserEvent, sessionRecorder, SESSION_EVENTS } from '@/services/sessionRecorder'

  export default {
    name: 'RatingTransaction',
    components: {
      ModalCloseButton
    },
    props: {
      pointName: {
        type: String,
        default: ''
      },
      token: {
        type: String,
        default: ''
      },
      transactionId: {
        type: Number,
        default: 0
      }
    },
    emits: ['confirmAction'],
    data: function () {
      return {
        rating: null,
        showThanksMessage: false,
        feedback: ''
      }
    },
    methods: {
      confirmAction() {
        this.showThanksMessage = true
        this.sendRating()
        setTimeout(() => {
          this.$emit('confirmAction')
        }, 3000)
      },
      async sendRating() {
        let params = {
          transaction_id: this.transactionId,
          rating: this.rating,
          feedback: this.feedback
        }
        try {
          await API.post('transaction/evaluation', params)
          sessionRecorder.addUserEvent(
            new UserEvent(SESSION_EVENTS.EVALUATED_TRANSACTION, this.transactionId ?? null, {
              rating: this.rating,
              feedback: this.feedback
            })
          )
        } catch (error) {
          handleError(error)
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .rating-sheet-wrapper {
    position: fixed;
    inset: 0;
    z-index: 10000;
  }

  .rating-sheet-backdrop {
    position: absolute;
    inset: 0;
    background: rgb(0 0 0 / 35%);
  }

  .rating-sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--app-fixed-bottom-offset, 0px);
    background: #fff;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgb(0 0 0 / 15%);
    max-height: calc(var(--app-viewport-safe-height, 100vh) - 96px);
    max-height: calc(var(--app-viewport-safe-height, 100dvh) - 96px);
    overflow-y: auto;
  }

  .sheet-content {
    position: relative;
    padding: 24px;
    padding-top: 30px;
  }

  .sheet-header {
    text-align: center;
    margin-bottom: 18px;
  }

  .sheet-title {
    font-size: 18px;
    font-weight: 700;
    color: #303030;
    margin: 0;
  }

  .sheet-subtitle {
    margin: 6px 0 0;
    color: #636b74;
    font-size: 13px;
    font-weight: 500;
  }

  .dialog-minimize-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 2;
  }

  .rating-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .stars-row {
    display: flex;
    justify-content: center;
    gap: 6px;
  }

  .star-btn {
    border: none;
    background: transparent;
    padding: 4px;
    cursor: pointer;
  }

  .star-icon {
    font-size: 30px;
  }

  .star-on {
    color: #ffc629;
  }

  .star-off {
    color: #d1d5db;
  }

  .feedback-input {
    width: 100%;
    min-height: 110px;
    resize: none;
    border: 1px solid #d6dde5;
    border-radius: 12px;
    padding: 12px;
    font-size: 14px;
    line-height: 1.45;
    color: #303030;
  }

  .feedback-input:focus {
    outline: none;
    border-color: #f47c20;
    box-shadow: 0 0 0 3px rgb(244 124 32 / 15%);
  }

  .actions-row {
    display: flex;
    justify-content: flex-end;
  }

  .send-btn {
    min-width: 140px;
    font-weight: 700;
  }

  .thanks-state {
    min-height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .thanks-icon {
    color: #17a34a;
    font-size: 36px;
  }

  .thanks-text {
    margin: 0;
    font-size: 15px;
    color: #374151;
    text-align: center;
  }

  @media (max-width: 480px) {
    .sheet-content {
      padding: 16px;
      padding-top: 26px;
    }

    .sheet-title {
      font-size: 16px;
    }

    .star-icon {
      font-size: 28px;
    }
  }
</style>
